import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import Layout from '@/components/Layout'
import { apiFetch } from '@/utils/api'
import { formatAmount, formatDate } from '@/utils/formatters'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import PaginationFooter from '@/components/PaginationFooter'
import { ArrowDown, ArrowUp, Search } from 'lucide-react'

type LinkType = 'invoice_payment' | 'order_payment' | 'payment_settlement'
type LinkStatus = 'active' | 'manual_review' | 'rejected' | 'superseded'
type MatchedBy = 'system' | 'user'
type SettlementType = 'bank' | 'cash_register_day_end'

interface ReconciliationLinkRow {
  id: string
  link_type: LinkType
  status: LinkStatus
  match_type?: string | null
  confidence?: number | string | null
  rule_name?: string | null
  rule_version?: string | null
  explanation?: string | null
  matched_by: MatchedBy
  run_id?: string | null
  invoice_id?: string | null
  order_id?: string | null
  payment_id?: string | null
  settlement_id?: string | null
  settlement_type?: SettlementType | null
  allocated_amount?: number | string | null
  created_at: string
  updated_at?: string | null
  invoice_number?: string | null
  invoice_vendor_name?: string | null
  invoice_total_amount?: number | string | null
  invoice_currency?: string | null
  order_external_transaction_id?: string | null
  order_external_order_id?: string | null
  order_total_amount?: number | string | null
  order_line_items?: Array<{
    item_name?: string | null
    quantity?: number | string | null
    total_price?: number | string | null
  }> | null
  payment_invoice_reference?: string | null
  payment_external_id?: string | null
  payment_amount?: number | string | null
  payment_currency?: string | null
  settlement_bank_reference?: string | null
  settlement_description?: string | null
}

interface ReconciliationPathParity {
  canonical_count: number
  active_link_count: number
  missing_active_links: number
  extra_active_links: number
  ambiguous_candidates: number
  ambiguous_without_manual_review: number
}

interface ReconciliationShadowReport {
  generated_at: string
  invoice_payment: ReconciliationPathParity
  order_payment: ReconciliationPathParity
  payment_settlement: ReconciliationPathParity
  settlement_links_missing_type: number
}

interface PaginationState {
  page: number
  limit: number
  pages: number
  total: number
}

interface FiltersState {
  search: string
  linkType: 'all' | LinkType
  status: 'all' | LinkStatus
  matchedBy: 'all' | MatchedBy
  settlementType: 'all' | SettlementType
  runId: string
}

const getStatusVariant = (status: LinkStatus): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (status === 'active') return 'default'
  if (status === 'manual_review') return 'secondary'
  if (status === 'rejected') return 'destructive'
  return 'outline'
}

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-'
  return new Date(value).toLocaleString()
}

const shortId = (value?: string | null): string => {
  if (!value) return '-'
  return value.slice(0, 8)
}

const renderEntityIdLink = (href: string, id?: string | null) => {
  if (!id) return '-'
  return (
    <a
      className="underline underline-offset-2 hover:text-primary"
      href={href}
      onClick={(event) => event.stopPropagation()}
    >
      {shortId(id)}
    </a>
  )
}

function ReconciliationPage() {
  const [rows, setRows] = useState<ReconciliationLinkRow[]>([])
  const [selectedRow, setSelectedRow] = useState<ReconciliationLinkRow | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [loadingShadow, setLoadingShadow] = useState<boolean>(true)
  const [shadow, setShadow] = useState<ReconciliationShadowReport | null>(null)
  const [filters, setFilters] = useState<FiltersState>({
    search: '',
    linkType: 'all',
    status: 'all',
    matchedBy: 'all',
    settlementType: 'all',
    runId: '',
  })
  const [debouncedSearch, setDebouncedSearch] = useState<string>('')
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    limit: 50,
    pages: 1,
    total: 0,
  })
  const [sortBy, setSortBy] = useState<'created_at' | 'confidence' | 'status'>('created_at')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search)
    }, 250)
    return () => clearTimeout(timer)
  }, [filters.search])

  useEffect(() => {
    fetchLinks()
  }, [
    pagination.page,
    pagination.limit,
    debouncedSearch,
    filters.linkType,
    filters.status,
    filters.matchedBy,
    filters.settlementType,
    filters.runId,
    sortBy,
    sortDir,
  ])

  useEffect(() => {
    fetchShadowReport()
  }, [])

  const hasFilters = useMemo(() => {
    return Boolean(
      filters.search ||
      filters.runId ||
      filters.linkType !== 'all' ||
      filters.status !== 'all' ||
      filters.matchedBy !== 'all' ||
      filters.settlementType !== 'all'
    )
  }, [filters])

  const fetchLinks = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        sort_by: sortBy,
        sort_dir: sortDir,
      })

      if (debouncedSearch) params.append('search', debouncedSearch)
      if (filters.linkType !== 'all') params.append('link_type', filters.linkType)
      if (filters.status !== 'all') params.append('status', filters.status)
      if (filters.matchedBy !== 'all') params.append('matched_by', filters.matchedBy)
      if (filters.settlementType !== 'all') params.append('settlement_type', filters.settlementType)
      if (filters.runId.trim()) params.append('run_id', filters.runId.trim())

      const response = await apiFetch(`/api/payments/reconciliation-links?${params.toString()}`)
      if (!response.ok) {
        setRows([])
        return
      }

      const data = await response.json()
      setRows(data.items || [])
      setPagination((prev) => ({
        ...prev,
        total: data.total || 0,
        pages: data.pagination?.pages || 1,
      }))
    } catch (error) {
      console.error('Failed to fetch reconciliation links', error)
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  const fetchShadowReport = async () => {
    setLoadingShadow(true)
    try {
      const response = await apiFetch('/api/payments/reconciliation-links/shadow-report')
      if (!response.ok) {
        setShadow(null)
        return
      }
      const data = await response.json()
      setShadow(data)
    } catch (error) {
      console.error('Failed to fetch reconciliation shadow report', error)
      setShadow(null)
    } finally {
      setLoadingShadow(false)
    }
  }

  const handleFilterChange = (key: keyof FiltersState, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const clearFilters = () => {
    setFilters({
      search: '',
      linkType: 'all',
      status: 'all',
      matchedBy: 'all',
      settlementType: 'all',
      runId: '',
    })
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const toggleSort = (column: 'created_at' | 'confidence' | 'status') => {
    const nextDir: 'asc' | 'desc' = sortBy === column && sortDir === 'asc' ? 'desc' : 'asc'
    setSortBy(column)
    setSortDir(nextDir)
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const sortIcon = (column: 'created_at' | 'confidence' | 'status') => {
    if (sortBy !== column) return null
    return sortDir === 'asc' ? <ArrowUp className="h-3 w-3 inline ml-1" /> : <ArrowDown className="h-3 w-3 inline ml-1" />
  }

  const renderPathCard = (title: string, path?: ReconciliationPathParity) => {
    return (
      <div className="rounded-lg border p-4 bg-card">
        <div className="text-sm text-muted-foreground">{title}</div>
        {!path ? (
          <div className="text-sm mt-2">-</div>
        ) : (
          <div className="mt-2 space-y-1 text-sm">
            <div>Canonical: <span className="font-semibold">{path.canonical_count}</span></div>
            <div>Active links: <span className="font-semibold">{path.active_link_count}</span></div>
            <div>Missing: <span className="font-semibold">{path.missing_active_links}</span></div>
            <div>Extra: <span className="font-semibold">{path.extra_active_links}</span></div>
            <div>Ambiguous: <span className="font-semibold">{path.ambiguous_candidates}</span></div>
            <div>Missing manual: <span className="font-semibold">{path.ambiguous_without_manual_review}</span></div>
          </div>
        )}
      </div>
    )
  }

  const renderSettlementAmount = (row: ReconciliationLinkRow): string => {
    if (row.allocated_amount != null) {
      return formatAmount(row.allocated_amount, { currencyCode: row.payment_currency || undefined })
    }
    if (row.payment_amount != null) {
      return formatAmount(row.payment_amount, { currencyCode: row.payment_currency || undefined })
    }
    return '-'
  }

  const renderRecordPanel = (
    title: string,
    href: string | null,
    id: string | null | undefined,
    details: Array<{ label: string; value: string | null | undefined }>,
    extraContent?: ReactNode
  ) => (
    <div className="rounded-lg border p-4 bg-muted/20">
      <div className="text-sm font-semibold mb-2">{title}</div>
      <div className="text-xs mb-3">
        <span className="text-muted-foreground">ID: </span>
        {href && id ? renderEntityIdLink(href, id) : <span>{id ? shortId(id) : '-'}</span>}
      </div>
      <div className="space-y-1 text-sm">
        {details.map((detail) => (
          <div key={`${title}-${detail.label}`}>
            <span className="text-muted-foreground">{detail.label}: </span>
            <span>{detail.value || '-'}</span>
          </div>
        ))}
      </div>
      {extraContent}
    </div>
  )

  const renderReconciledRecords = (row: ReconciliationLinkRow) => {
    if (row.link_type === 'invoice_payment') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderRecordPanel('Invoice', row.invoice_id ? `/invoices/${row.invoice_id}` : null, row.invoice_id, [
            { label: 'Invoice number', value: row.invoice_number || null },
            { label: 'Vendor', value: row.invoice_vendor_name || null },
            {
              label: 'Amount',
              value: row.invoice_total_amount != null
                ? formatAmount(row.invoice_total_amount, { currencyCode: row.invoice_currency || row.payment_currency || undefined })
                : null,
            },
          ])}
          {renderRecordPanel('Payment', row.payment_id ? `/payments/${row.payment_id}` : null, row.payment_id, [
            { label: 'Reference', value: row.payment_invoice_reference || null },
            { label: 'External ID', value: row.payment_external_id || null },
            {
              label: 'Amount',
              value: row.payment_amount != null ? formatAmount(row.payment_amount, { currencyCode: row.payment_currency || undefined }) : null,
            },
          ])}
        </div>
      )
    }

    if (row.link_type === 'order_payment') {
      const orderLineItems = row.order_line_items || []
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderRecordPanel('Order', row.order_id ? `/sales/orders/${row.order_id}` : null, row.order_id, [
            { label: 'Transaction ID', value: row.order_external_transaction_id || null },
            { label: 'Order external ID', value: row.order_external_order_id || null },
            {
              label: 'Amount',
              value: row.order_total_amount != null ? formatAmount(row.order_total_amount, { currencyCode: row.payment_currency || undefined }) : null,
            },
          ],
          orderLineItems.length > 0 ? (
            <div className="mt-3 border-t pt-3">
              <div className="text-xs font-semibold text-muted-foreground mb-2">Line items</div>
              <div className="space-y-1 text-xs">
                {orderLineItems.map((item, index) => (
                  <div key={`${row.id}-line-item-${index}`} className="flex justify-between gap-3">
                    <span className="truncate">
                      {(item.item_name || 'Unnamed item')}
                      {item.quantity != null ? ` x ${item.quantity}` : ''}
                    </span>
                    <span>
                      {item.total_price != null
                        ? formatAmount(item.total_price, { currencyCode: row.payment_currency || undefined })
                        : '-'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : undefined)}
          {renderRecordPanel('Payment', row.payment_id ? `/payments/${row.payment_id}` : null, row.payment_id, [
            { label: 'Reference', value: row.payment_invoice_reference || null },
            { label: 'External ID', value: row.payment_external_id || null },
            {
              label: 'Amount',
              value: row.payment_amount != null ? formatAmount(row.payment_amount, { currencyCode: row.payment_currency || undefined }) : null,
            },
          ])}
        </div>
      )
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderRecordPanel('Payment', row.payment_id ? `/payments/${row.payment_id}` : null, row.payment_id, [
          { label: 'Reference', value: row.payment_invoice_reference || null },
          { label: 'External ID', value: row.payment_external_id || null },
          {
            label: 'Amount',
            value: row.payment_amount != null ? formatAmount(row.payment_amount, { currencyCode: row.payment_currency || undefined }) : null,
          },
        ])}
        {renderRecordPanel(
          'Settlement',
          row.settlement_id ? `/bank-transactions?search=${row.settlement_id}` : null,
          row.settlement_id,
          [
            { label: 'Type', value: row.settlement_type || null },
            { label: 'Reference', value: row.settlement_bank_reference || null },
            { label: 'Description', value: row.settlement_description || null },
            { label: 'Amount', value: renderSettlementAmount(row) },
          ]
        )}
      </div>
    )
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Reconciliation</h1>
            <p className="text-muted-foreground">
              Inspect and search explicit reconciliation links with shadow parity diagnostics.
            </p>
          </div>
          <Button variant="outline" onClick={fetchShadowReport} disabled={loadingShadow}>
            Refresh shadow report
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {renderPathCard('Invoice <> Payment', shadow?.invoice_payment)}
          {renderPathCard('Order <> Payment', shadow?.order_payment)}
          {renderPathCard('Payment <> Settlement', shadow?.payment_settlement)}
        </div>

        <div className="rounded-lg border p-4 bg-card text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              Generated at: <span className="font-medium">{formatDateTime(shadow?.generated_at)}</span>
            </div>
            <div>
              Settlement links missing type: <span className="font-medium">{shadow?.settlement_links_missing_type ?? '-'}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="relative min-w-[260px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search IDs, references, rule names, explanations..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>

          <Input
            className="w-44"
            placeholder="Run ID"
            value={filters.runId}
            onChange={(e) => handleFilterChange('runId', e.target.value)}
          />

          <Select value={filters.linkType} onValueChange={(value) => handleFilterChange('linkType', value)}>
            <SelectTrigger className="w-[190px]">
              <SelectValue placeholder="Link type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All link types</SelectItem>
              <SelectItem value="invoice_payment">invoice_payment</SelectItem>
              <SelectItem value="order_payment">order_payment</SelectItem>
              <SelectItem value="payment_settlement">payment_settlement</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.status} onValueChange={(value) => handleFilterChange('status', value)}>
            <SelectTrigger className="w-[170px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">active</SelectItem>
              <SelectItem value="manual_review">manual_review</SelectItem>
              <SelectItem value="rejected">rejected</SelectItem>
              <SelectItem value="superseded">superseded</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.matchedBy} onValueChange={(value) => handleFilterChange('matchedBy', value)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Matched by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All matchers</SelectItem>
              <SelectItem value="system">system</SelectItem>
              <SelectItem value="user">user</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.settlementType} onValueChange={(value) => handleFilterChange('settlementType', value)}>
            <SelectTrigger className="w-[190px]">
              <SelectValue placeholder="Settlement type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All settlements</SelectItem>
              <SelectItem value="bank">bank</SelectItem>
              <SelectItem value="cash_register_day_end">cash_register_day_end</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" onClick={clearFilters} disabled={!hasFilters}>
            Clear filters
          </Button>
        </div>

        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('created_at')}>
                  Created {sortIcon('created_at')}
                </TableHead>
                <TableHead>Link</TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('status')}>
                  Status {sortIcon('status')}
                </TableHead>
                <TableHead>Matched</TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('confidence')}>
                  Confidence {sortIcon('confidence')}
                </TableHead>
                <TableHead>Rule</TableHead>
                <TableHead>Entities</TableHead>
                <TableHead>Settlement</TableHead>
                <TableHead>Run</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    Loading reconciliation links...
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    No reconciliation links found.
                  </TableCell>
                </TableRow>
              ) : rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer hover:bg-muted/40"
                  onClick={() => setSelectedRow(row)}
                >
                  <TableCell className="whitespace-nowrap">{formatDateTime(row.created_at)}</TableCell>
                  <TableCell><Badge variant="outline">{row.link_type}</Badge></TableCell>
                  <TableCell><Badge variant={getStatusVariant(row.status)}>{row.status}</Badge></TableCell>
                  <TableCell>{row.matched_by}</TableCell>
                  <TableCell>{row.confidence == null ? '-' : Number(row.confidence).toFixed(4)}</TableCell>
                  <TableCell className="max-w-[260px]">
                    <div className="font-medium truncate">{row.rule_name || '-'}</div>
                    {row.explanation && <div className="text-xs text-muted-foreground truncate">{row.explanation}</div>}
                  </TableCell>
                  <TableCell className="max-w-[320px] text-xs">
                    {row.invoice_id && (
                      <div>
                        Invoice ID: {renderEntityIdLink(`/invoices/${row.invoice_id}`, row.invoice_id)}
                        {row.invoice_number ? ` (${row.invoice_number})` : ''}
                      </div>
                    )}
                    {row.order_id && (
                      <div>
                        Order ID: {renderEntityIdLink(`/sales/orders/${row.order_id}`, row.order_id)}
                        {row.order_external_transaction_id ? ` (tx ${row.order_external_transaction_id})` : ''}
                      </div>
                    )}
                    {row.payment_id && (
                      <div>
                        Payment ID: {renderEntityIdLink(`/payments/${row.payment_id}`, row.payment_id)}
                        {row.payment_invoice_reference ? ` (ref ${row.payment_invoice_reference})` : ''}
                      </div>
                    )}
                    {!row.invoice_id && !row.order_id && !row.payment_id && <div>-</div>}
                  </TableCell>
                  <TableCell className="text-xs">
                    <div>Type: {row.settlement_type || '-'}</div>
                    <div>
                      Ref: {row.settlement_bank_reference || renderEntityIdLink(`/bank-transactions?search=${row.settlement_id}`, row.settlement_id)}
                    </div>
                    <div>
                      Amount: {renderSettlementAmount(row)}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">
                    <div>{row.run_id || '-'}</div>
                    <div className="text-muted-foreground">{formatDate(row.created_at)}</div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <PaginationFooter
          pagination={pagination}
          onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
          onPageSizeChange={(limit) => setPagination((prev) => ({ ...prev, limit, page: 1 }))}
          pageSizeOptions={[20, 50, 100, 200]}
        />

        <Dialog open={Boolean(selectedRow)} onOpenChange={(open) => !open && setSelectedRow(null)}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            {selectedRow && (
              <>
                <DialogHeader>
                  <DialogTitle>Reconciliation link {shortId(selectedRow.id)}</DialogTitle>
                  <DialogDescription>
                    {selectedRow.link_type} • {selectedRow.status} • {formatDateTime(selectedRow.created_at)}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  {renderReconciledRecords(selectedRow)}
                  <div className="rounded-lg border p-4 text-sm space-y-1">
                    <div><span className="text-muted-foreground">Rule: </span>{selectedRow.rule_name || '-'}</div>
                    <div><span className="text-muted-foreground">Match type: </span>{selectedRow.match_type || '-'}</div>
                    <div>
                      <span className="text-muted-foreground">Confidence: </span>
                      {selectedRow.confidence == null ? '-' : Number(selectedRow.confidence).toFixed(4)}
                    </div>
                    <div><span className="text-muted-foreground">Run ID: </span>{selectedRow.run_id || '-'}</div>
                    <div><span className="text-muted-foreground">Explanation: </span>{selectedRow.explanation || '-'}</div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  )
}

export const Route = createFileRoute('/finance/reconciliation')({
  component: ReconciliationPage,
})
